import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text, Box } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate } from '../../../lib/format';
import { TenantName } from '../../../components/TenantName';
import { PersonAvatar } from './PersonAvatar';
import { useT } from '../../../i18n/I18nProvider';

type AdminUser = NonNullable<Awaited<ReturnType<typeof admin.listUsers>>['data']>[number];

export function UsersList({ records }: { records: AdminUser[] }) {
  const navigate = useNavigate();
  const t = useT();
  const unnamed = t('users.unnamed');
  return (
    <Stack gap="sm">
      {records.map((u) => (
        <button
          key={u.id}
          type="button"
          onClick={() => navigate(`/users/${u.id}`)}
          className="rr-list-row"
        >
          <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
            <span style={{ flexShrink: 0, display: 'inline-flex' }}>
              <PersonAvatar name={u.display_name || u.email || unnamed} size="md" />
            </span>
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Group gap="xs" align="baseline" wrap="nowrap">
                <Text role="subtitle" fw={600} truncate>{u.display_name || u.email || unnamed}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)" truncate>{u.email ?? ''}</Text>
              </Group>
              <Group gap="xs" align="baseline" wrap="nowrap" style={{ minWidth: 0 }}>
                <Text role="caption" c="var(--rr-color-text-muted)">{t('common.tenant')}</Text>
                <TenantName id={u.organisation_id} subtle />
                <Text role="caption" c="var(--rr-color-text-muted)">
                  · {t('users.list.created', { date: formatDate(u.created_at) })}
                </Text>
              </Group>
            </Stack>
            <Box style={{ flexShrink: 0 }}>
              <StatusBadge size="sm" status={u.scope === 'platform' ? 'warning' : 'pending'}>
                {u.scope === 'platform' ? t('users.scope.platform') : t('users.scope.tenant')}
              </StatusBadge>
            </Box>
          </Group>
        </button>
      ))}
    </Stack>
  );
}
